import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Loader2, Save } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface Props {
  companyId: string;
}

type NamingMode = "standard" | "original_numbered";

/**
 * Pestaña "Ajustes" del maestro de empresa.
 *
 * Por ahora expone un único ajuste: el formato del nombre de los documentos
 * que se descargan (individualmente o en ZIP). Sólo afecta a esta empresa;
 * el resto siguen con la convención estándar.
 */
const CompanySettingsTab = ({ companyId }: Props) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [mode, setMode] = useState<NamingMode>("standard");

  useEffect(() => {
    if (!companyId) return;
    setLoading(true);
    supabase
      .from("company_masters")
      .select("naming_mode")
      .eq("id", companyId)
      .single()
      .then(({ data }) => {
        const value = (data as any)?.naming_mode === "original_numbered" ? "original_numbered" : "standard";
        setMode(value);
        setLoading(false);
      });
  }, [companyId]);

  const handleSave = async () => {
    setSaving(true);
    const { error } = await supabase
      .from("company_masters")
      .update({ naming_mode: mode } as any)
      .eq("id", companyId);
    setSaving(false);
    if (error) {
      toast({ title: "Error al guardar", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "Ajustes guardados" });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 text-sm text-muted-foreground">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Cargando…
      </div>
    );
  }

  return (
    <div className="max-w-2xl space-y-6">
      <div className="space-y-3">
        <Label className="text-xs uppercase tracking-wider text-muted-foreground">
          Nombre de archivo para descargas
        </Label>
        <p className="text-xs text-muted-foreground">
          Determina cómo se nombran los documentos al descargarlos (individualmente o en ZIP).
          Sólo afecta a esta empresa; el resto siguen con la convención estándar.
        </p>

        <RadioGroup value={mode} onValueChange={(v) => setMode(v as NamingMode)} className="space-y-3 pt-2">
          <label
            htmlFor="mode-standard"
            className="flex cursor-pointer items-start gap-3 rounded-md border bg-card p-4 hover:bg-accent/30"
          >
            <RadioGroupItem id="mode-standard" value="standard" className="mt-1" />
            <div className="space-y-1">
              <div className="text-sm font-medium">Estándar</div>
              <div className="text-xs text-muted-foreground">
                Formato actual: <code className="rounded bg-muted px-1 py-0.5 font-mono">FECHA_Proveedor_NºFactura_MATRÍCULA_PAGO.pdf</code>
              </div>
            </div>
          </label>

          <label
            htmlFor="mode-original"
            className="flex cursor-pointer items-start gap-3 rounded-md border bg-card p-4 hover:bg-accent/30"
          >
            <RadioGroupItem id="mode-original" value="original_numbered" className="mt-1" />
            <div className="space-y-1">
              <div className="text-sm font-medium">Nº de IVA + nombre original</div>
              <div className="text-xs text-muted-foreground">
                Mantiene el nombre original del PDF y le añade delante el Nº de IVA (sin ceros).
                Ejemplo: <code className="rounded bg-muted px-1 py-0.5 font-mono">1_Factura abril.pdf</code>,{" "}
                <code className="rounded bg-muted px-1 py-0.5 font-mono">27_FRA-2025-001.pdf</code>
              </div>
            </div>
          </label>
        </RadioGroup>
      </div>

      <div className="flex justify-end">
        <Button
          onClick={handleSave}
          disabled={saving}
          className="gap-2 bg-[hsl(var(--brand-green))] hover:bg-[hsl(152,40%,45%)] text-white"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          Guardar ajustes
        </Button>
      </div>
    </div>
  );
};

export default CompanySettingsTab;
